import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';

import {
    AttributesService,
    IpsecProposalServiceV2,
    IpsecTunnelerServiceV2,
} from '@netfoundry-ui/shared/apiv2';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    Environment,
    ENVIRONMENT,
    IpsecProposal,
    IpsecTunnelerV2,
    NetworkV2,
    PagedAttributes,
} from '@netfoundry-ui/shared/model';
import {
    ApiService,
    FeatureService,
    LoggerService,
    ValidateService,
} from '@netfoundry-ui/shared/services';
import copy from 'copy-to-clipboard';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Component({
    selector: 'app-ziti-ipsec-tunneler',
    templateUrl: './ziti-ipsec-tunneler.component.html',
    styleUrls: ['./ziti-ipsec-tunneler.component.scss'],
    standalone: false,
})
export class ZitiIpsecTunnelerComponent implements OnInit, OnDestroy {
    public model: IpsecTunnelerV2 = new IpsecTunnelerV2();

    invalidWords = ['#all'];
    isComplete = false;
    processing = false;
    isLoading = false;
    isLoadingAttr = false;

    showMore = false;

    ikeProposalCatalog: IpsecProposal[] = [];
    espProposalCatalog: IpsecProposal[] = [];
    ikeProposalSelected: { [value: string]: boolean } = {};
    espProposalSelected: { [value: string]: boolean } = {};
    proposalsLoading = false;
    // True if the catalog fetch errored OR returned empty. Submit is hard-blocked
    // because a silent fallback to server defaults would hide a real ops issue
    // (catalog endpoint down, DB seed missing, etc.).
    proposalsLoadFailed = false;

    invalidName = false;

    tunnelerIdentityName = '';

    // Catalog fetched from the Ziti controller's identity-role-attributes endpoint
    // — tunnelers create a ziti identity, so role attributes are identity-side.
    identityRoleAttributes = new PagedAttributes();
    selectedAttributes = new PagedAttributes();
    attributeError = false;

    // Revealed exactly once on the create response. Never re-fetched.
    pskRevealed: string | undefined;
    pskCopied = false;

    errors: { [k: string]: string } = {};

    currentNetwork: NetworkV2;
    private subscription = new Subscription();

    constructor(
        private logger: LoggerService,
        public ipsecTunnelerService: IpsecTunnelerServiceV2,
        public ipsecProposalService: IpsecProposalServiceV2,
        private attributeService: AttributesService,
        public growlerService: GrowlerService,
        public authorizationService: AuthorizationService,
        public validateService: ValidateService,
        private apiService: ApiService,
        public featureService: FeatureService,
        private router: Router,
        @Inject(ZITI_DATA_SERVICE) private zitiDataService: ZitiDataService,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {}

    private navigateBackToList() {
        this.router.navigate(['/network/ipsec-tunnelers']);
    }

    ngOnInit() {
        this.isLoading = true;
        this.initModel();
        this.currentNetwork = this.apiService.currentNetwork.getValue();
        if (this.currentNetwork) {
            this.model.networkId = this.currentNetwork.id;
            this.initializeAttributeSelector();
            this.model.provider = 'AWS';
        }

        this.loadProposalCatalogs();
    }

    private loadProposalCatalogs() {
        this.proposalsLoading = true;
        this.proposalsLoadFailed = false;
        Promise.all([
            this.ipsecProposalService.getIkeProposals(),
            this.ipsecProposalService.getEspProposals(),
        ])
            .then(([ike, esp]) => {
                this.ikeProposalCatalog = ike || [];
                this.espProposalCatalog = esp || [];
                if (this.ikeProposalCatalog.length === 0
                    || this.espProposalCatalog.length === 0) {
                    this.proposalsLoadFailed = true;
                    return;
                }
                this.ikeProposalSelected = this.allChecked(this.ikeProposalCatalog);
                this.espProposalSelected = this.allChecked(this.espProposalCatalog);
            })
            .catch((err) => {
                this.logger.error('Failed to load IPsec proposal catalogs', err);
                this.proposalsLoadFailed = true;
            })
            .finally(() => {
                this.proposalsLoading = false;
            });
    }

    private allChecked(catalog: IpsecProposal[]): { [value: string]: boolean } {
        const result: { [value: string]: boolean } = {};
        catalog.forEach((p) => {
            result[p.value] = true;
        });
        return result;
    }

    onToggleIkeProposal(value: string) {
        this.ikeProposalSelected[value] = !this.ikeProposalSelected[value];
    }

    onToggleEspProposal(value: string) {
        this.espProposalSelected[value] = !this.espProposalSelected[value];
    }

    /** Returns proposal `value`s that are currently checked, in sortOrder ASC. */
    private collectSelectedProposals(
        catalog: IpsecProposal[],
        selected: { [value: string]: boolean }
    ): string[] {
        return catalog
            .slice()
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .filter((p) => !!selected[p.value])
            .map((p) => p.value);
    }

    get ikeSelectionCount(): number {
        return Object.values(this.ikeProposalSelected).filter((v) => !!v).length;
    }

    get espSelectionCount(): number {
        return Object.values(this.espProposalSelected).filter((v) => !!v).length;
    }

    get hasMinimumProposals(): boolean {
        if (this.proposalsLoading || this.proposalsLoadFailed) {
            return false;
        }
        return this.ikeSelectionCount >= 1 && this.espSelectionCount >= 1;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
        this.hide();
    }

    hide() {
        this.navigateBackToList();
    }

    headerActionRequested(action: { name: string }) {
        if (action?.name === 'close') {
            this.hide();
        } else if (action?.name === 'save') {
            this.save();
        }
    }

    onNameChange() {
        this.tunnelerIdentityName = this.model.name ? `${this.model.name}-tunneler` : '';
    }

    isValidIPv4(ip: string): boolean {
        if (!ip) return false;
        const re = /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/;
        return re.test(ip);
    }

    isValidCidr(cidr: string): boolean {
        if (!cidr) return false;
        const re = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
        if (!re.test(cidr)) return false;
        const [ip, mask] = cidr.split('/');
        const m = parseInt(mask, 10);
        return this.isValidIPv4(ip) && m >= 0 && m <= 32;
    }

    customerPeerCidrInput = '';

    addCidr(value: string) {
        const cidr = (value || '').trim();
        if (!cidr) {
            this.errors['customerPeerCidrs'] = 'A peer CIDR is required';
            return;
        }
        if (!this.isValidCidr(cidr)) {
            this.errors['customerPeerCidrs'] = `${cidr} is not a valid CIDR`;
            return;
        }
        if (!Array.isArray(this.model.customerPeerCidrs)) {
            this.model.customerPeerCidrs = [];
        }
        if (this.model.customerPeerCidrs.includes(cidr)) {
            this.customerPeerCidrInput = '';
            delete this.errors['customerPeerCidrs'];
            return;
        }
        this.model.customerPeerCidrs.push(cidr);
        this.customerPeerCidrInput = '';
        delete this.errors['customerPeerCidrs'];
    }

    removeCidr(value: string) {
        if (!Array.isArray(this.model.customerPeerCidrs)) return;
        this.model.customerPeerCidrs = this.model.customerPeerCidrs.filter((c) => c !== value);
    }

    private getSelectedFromModel() {
        const tmp = new PagedAttributes();
        (this.model.attributes || []).forEach((attr) => {
            tmp.mappedAtrributes.set(attr, { name: attr, isGroup: true });
        });
        this.selectedAttributes = tmp;
    }

    addAttribute(newAttribute: string) {
        const success = this.attributeService.addAttributes(
            newAttribute,
            this.invalidWords,
            this.selectedAttributes
        );
        this.model.attributes = Array.from(this.selectedAttributes.mappedAtrributes.keys());
        if (success) {
            this.selectedAttributes = success as PagedAttributes;
        } else {
            this.attributeError = true;
            this.errors['attributes'] = `attribute name not allowed: ${newAttribute}`;
        }
    }

    removeAttribute(oldAttribute: string) {
        this.selectedAttributes = this.attributeService.removeAttribute(
            oldAttribute,
            this.selectedAttributes
        );
        this.model.attributes = Array.from(this.selectedAttributes.mappedAtrributes.keys());
    }

    private initializeAttributeSelector() {
        this.isLoadingAttr = true;
        this.getSelectedFromModel();
        // Identity-side attributes — the tunneler is bound to a Ziti identity, not an edge router.
        this.zitiDataService
            .get('identity-role-attributes', {}, [])
            .then((result: any) => {
                const paged = new PagedAttributes();
                (result?.data || []).forEach((attr: string) => {
                    paged.mappedAtrributes.set(attr, { name: attr, isGroup: true });
                });
                this.identityRoleAttributes = paged;
            })
            .catch((err) => {
                this.logger.error(err);
            })
            .finally(() => {
                this.isLoadingAttr = false;
                this.isLoading = this.isLoadingAttr;
            });
    }

    async validate(): Promise<boolean> {
        this.errors = {};
        this.attributeError = false;

        // Auto-flush pending text in the Customer Peer CIDRs input into a pill
        // so the user doesn't have to hit Enter / + before Create. Valid text
        // is appended and the input cleared; invalid text is left in place and
        // surfaced as a CIDR error below (overriding the "required" message).
        const pendingCidr = this.customerPeerCidrInput?.trim();
        if (pendingCidr && this.isValidCidr(pendingCidr)) {
            if (!Array.isArray(this.model.customerPeerCidrs)) {
                this.model.customerPeerCidrs = [];
            }
            if (!this.model.customerPeerCidrs.includes(pendingCidr)) {
                this.model.customerPeerCidrs.push(pendingCidr);
            }
            this.customerPeerCidrInput = '';
        }

        if (!this.model.name) {
            this.errors['name'] = 'Name is required';
        }
        if (!this.isValidIPv4(this.model.customerPublicIp)) {
            this.errors['customerPublicIp'] = 'A valid IPv4 address is required';
        }
        if (!Array.isArray(this.model.customerPeerCidrs)
            || this.model.customerPeerCidrs.length === 0) {
            this.errors['customerPeerCidrs'] = 'At least one peer CIDR is required';
        } else {
            const bad = this.model.customerPeerCidrs.find((c) => !this.isValidCidr(c));
            if (bad) {
                this.errors['customerPeerCidrs'] = `${bad} is not a valid CIDR`;
            }
        }
        if (this.customerPeerCidrInput?.trim()) {
            this.errors['customerPeerCidrs'] =
                `${this.customerPeerCidrInput.trim()} is not a valid CIDR`;
        }
        if (!this.model.region) {
            this.errors['region'] = 'Region is required';
        }
        if (!this.model.size) {
            this.errors['size'] = 'VM size is required';
        }

        if (this.ikeProposalCatalog.length > 0 && this.ikeSelectionCount < 1) {
            this.errors['ikeProposals'] = 'At least one IKE proposal must be selected';
        }
        if (this.espProposalCatalog.length > 0 && this.espSelectionCount < 1) {
            this.errors['espProposals'] = 'At least one ESP proposal must be selected';
        }

        if (Object.keys(this.errors).length > 0) {
            return false;
        }

        // Server-side dry-run via nf-validate. Errors on server-only fields
        // (e.g. networkId, auth checks) have no inline UI, so they're also
        // surfaced via growler.
        try {
            const errors = await this.ipsecTunnelerService.validateCreate(this.model);
            if (!_.isArray(errors)) return false;
            if (errors.length === 0) return true;

            const fieldMessages: Record<string, string[]> = {};
            errors.forEach((e: any) => {
                const field = e.path?.replace(/^\$\./, '') || 'general';
                (fieldMessages[field] ||= []).push(e.message);
            });
            Object.entries(fieldMessages).forEach(([field, msgs]) => {
                this.errors[field] = msgs.join('; ');
            });

            const messageHtml = errors
                .map((e: any) => {
                    const label = e.label || (e.path || '').replace(/^\$\./, '') || 'error';
                    return `<div><strong>${label}</strong>: ${e.message}</div>`;
                })
                .join('');
            this.growlerService.show(
                new GrowlerData(
                    'error',
                    'IPsec Tunneler validation failed',
                    `${errors.length} ${errors.length === 1 ? 'issue' : 'issues'} to fix`,
                    messageHtml
                )
            );
            return false;
        } catch (err) {
            this.logger.error('validateCreate failed', err);
            this.growlerService.show(
                new GrowlerData(
                    'error',
                    'IPsec Tunneler validation failed',
                    'Could not reach the validation endpoint. Check your connection and try again.'
                )
            );
            return false;
        }
    }

    async save() {
        this.processing = true;
        this.model.attributes = Array.from(this.selectedAttributes.mappedAtrributes.keys());
        this.model.ikeProposals = this.collectSelectedProposals(
            this.ikeProposalCatalog,
            this.ikeProposalSelected
        );
        this.model.espProposals = this.collectSelectedProposals(
            this.espProposalCatalog,
            this.espProposalSelected
        );

        if (!(await this.validate())) {
            this.processing = false;
            return;
        }

        this.saveCreate();
    }

    private saveCreate() {
        this.ipsecTunnelerService.create(this.model).subscribe(
            (data: any) => {
                this.model = Object.assign(new IpsecTunnelerV2(), data);
                this.pskRevealed = data?.pskRevealedOnce;
                this.processing = false;
                this.isComplete = true;
                this.growlerService.show(
                    new GrowlerData(
                        'success',
                        'Success',
                        'IPsec Tunneler creation started',
                        'IPsec Tunneler create process started successfully. <a href="' +
                            this.router.createUrlTree(['/process-executions']).toString() +
                            '">Click here to find out more.</a>'
                    )
                );
                if (!this.pskRevealed) {
                    this.navigateBackToList();
                }
            },
            (httpErrorResponse) => {
                this.processing = false;
                this.growlerService.show(
                    new GrowlerData(
                        'error',
                        'IPsec Tunneler creation failed.',
                        httpErrorResponse?.error?.errors?.[0] || 'Unknown error'
                    )
                );
            }
        );
    }

    copyPsk() {
        if (!this.pskRevealed) return;
        copy(this.pskRevealed);
        this.pskCopied = true;
        this.growlerService.show(
            new GrowlerData('success', 'PSK Copied', '', 'PSK copied to clipboard. Store it now — it will not be shown again.')
        );
    }

    finishCreate() {
        this.navigateBackToList();
    }

    private initModel() {
        if (!this.model.attributes) this.model.attributes = [];
        if (!this.model.ikeProposals) this.model.ikeProposals = [];
        if (!this.model.espProposals) this.model.espProposals = [];
        if (!Array.isArray(this.model.customerPeerCidrs)) {
            this.model.customerPeerCidrs = [];
        }
        // Region (us-east-1: only provisioned IPsec VPC) and size (medium:
        // approved bridge VM size) are hardcoded for the POC but still required
        // on the create request. Restore template fields when multi-region /
        // multi-size becomes a real choice.
        if (!this.model.region) this.model.region = 'us-east-1';
        if (!this.model.size) this.model.size = 'medium';
    }
}
